import { createContext, useContext, useState } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import { css, cx, resetButtonStyle } from '@eti/styles';
import { Inline, Stack, vars } from '@etg/wings';
import ExpandableContent from '../expandable_content/ExpandableContent';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const accordionVariantStyles = {
  default: {
    button: css`
      color: ${vars.colors.links.default};
      text-decoration: underline;

      &:hover {
        color: ${vars.colors.links.focus};
      }

      &:focus-visible {
        outline: 0.125rem solid ${vars.colors.focusRing};
        outline-offset: 0.125rem;
      }
    `,
    headerContent: '',
    caretIcon: css`
      height: 1.25rem;
      width: 1.25rem;
    `
  },
  'full-width': {
    button: css`
      text-align: start;
      width: 100%;

      &:focus-visible {
        outline: 0.125rem solid ${vars.colors.focusRing};
        outline-offset: 0.25rem;
      }
    `,
    headerContent: css`
      flex: 1;
    `,
    caretIcon: css`
      height: 1.5rem;
      width: 1.5rem;
    `
  }
};
const caretStyles = (isExpanded, variant) => css`
  ${accordionVariantStyles[variant].caretIcon};
  transform: ${isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 150ms ease;
`;
const expandOnTopStyles = () => css`
  order: 1;
`;
const AccordionContext = /*#__PURE__*/createContext(null);
const AccordionBody = _ref => {
  let {
    children,
    className,
    ...rest
  } = _ref;
  const context = useContext(AccordionContext);
  if (!context) {
    return null;
  }
  return /*#__PURE__*/_jsx(ExpandableContent, {
    className: className,
    isExpanded: context.isExpanded,
    ...rest,
    children: /*#__PURE__*/_jsx("div", {
      "aria-labelledby": `${context.id}-button`,
      id: `${context.id}-body`,
      children: children
    })
  });
};
const AccordionHeader = _ref2 => {
  let {
    children,
    className,
    expandedHeader,
    ...rest
  } = _ref2;
  const context = useContext(AccordionContext);
  if (!context) {
    return null;
  }
  return /*#__PURE__*/_jsxs(Inline, {
    alignY: "center",
    "aria-controls": `${context.id}-body`,
    "aria-expanded": context.isExpanded,
    as: "button",
    className: cx(resetButtonStyle, accordionVariantStyles[context.variant].button, context.shouldExpandOnTop ? expandOnTopStyles() : '', className),
    id: `${context.id}-button`,
    onClick: context.toggleIsExpanded,
    type: "button",
    ...rest,
    children: [/*#__PURE__*/_jsx("div", {
      className: accordionVariantStyles[context.variant].headerContent,
      children: expandedHeader && context.isExpanded ? expandedHeader : children
    }), /*#__PURE__*/_jsx(CaretDown, {
      className: caretStyles(context.isExpanded, context.variant),
      "data-testid": "accordion-caret-icon"
    })]
  });
};
const Accordion = _ref3 => {
  let {
    children,
    id,
    isInitiallyExpanded = false,
    shouldExpandOnTop = false,
    spacing = 0,
    variant = 'default'
  } = _ref3;
  const [isExpanded, setIsExpanded] = useState(isInitiallyExpanded);
  const toggleIsExpanded = () => {
    setIsExpanded(prev => !prev);
  };
  return /*#__PURE__*/_jsx(AccordionContext.Provider, {
    value: {
      id,
      isExpanded,
      shouldExpandOnTop,
      toggleIsExpanded,
      variant
    },
    children: /*#__PURE__*/_jsx(Stack, {
      spacing: spacing,
      children: children
    })
  });
};
Accordion.Body = AccordionBody;
Accordion.Header = AccordionHeader;
export default Accordion;
//# sourceMappingURL=Accordion.js.map